import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FileText, 
  MessageSquare, 
  Mail, 
  Mic,
  Calendar,
  User,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Edit3,
  Filter,
  Search,
  Clock,
  Zap
} from 'lucide-react'
import { clsx } from 'clsx'

// 证据来源类型图标映射
const SOURCE_ICONS: Record<string, React.ElementType> = {
  meeting_note: FileText,
  MeetingNote: FileText,
  im: MessageSquare,
  IM: MessageSquare,
  email: Mail,
  Email: Mail,
  audio: Mic,
  Audio: Mic,
  doc: FileText,
  Doc: FileText,
  manual: FileText,
  Manual: FileText,
}

// 信号强度配色（支持大小写）
const SIGNAL_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  Strong: { bg: 'bg-green-100', text: 'text-green-700', label: '强信号' },
  strong: { bg: 'bg-green-100', text: 'text-green-700', label: '强信号' },
  Medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: '中信号' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: '中信号' },
  Weak: { bg: 'bg-gray-100', text: 'text-gray-600', label: '弱信号' },
  weak: { bg: 'bg-gray-100', text: 'text-gray-600', label: '弱信号' },
  None: { bg: 'bg-gray-50', text: 'text-gray-400', label: '无信号' },
  none: { bg: 'bg-gray-50', text: 'text-gray-400', label: '无信号' },
}

// 需求层级配色（支持大小写）
const DEMAND_LAYER_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  Solution: { bg: 'bg-blue-100', text: 'text-blue-700', label: '方案层' },
  solution: { bg: 'bg-blue-100', text: 'text-blue-700', label: '方案层' },
  Organization: { bg: 'bg-purple-100', text: 'text-purple-700', label: '组织层' },
  organization: { bg: 'bg-purple-100', text: 'text-purple-700', label: '组织层' },
  Personal: { bg: 'bg-pink-100', text: 'text-pink-700', label: '个人层' },
  personal: { bg: 'bg-pink-100', text: 'text-pink-700', label: '个人层' },
}

interface EvidenceItem {
  id: string
  sourceType: string
  contentRaw: string
  contentSummary?: string
  extractedTags?: {
    persons?: Array<{
      name: string
      role?: string
      attitude?: string
      title?: string
      department?: string
    }>
    facts?: Array<{
      category: string
      content: string
    }>
    riskFlags?: string[]
  }
  demandLayer?: string
  signalStrength?: string
  signalType?: string
  signalScore?: number
  status: string
  occurredAt: string
  createdAt: string
  sourceFileName?: string
}

interface EvidenceChainViewProps {
  evidences: EvidenceItem[]
  onEditEvidence?: (evidenceId: string) => void
  onFilterChange?: (filters: { signalStrength?: string; sourceType?: string }) => void
}

export default function EvidenceChainView({
  evidences,
  onEditEvidence,
}: EvidenceChainViewProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [filterSignal, setFilterSignal] = useState<string>('')
  const [filterSource, setFilterSource] = useState<string>('')

  // 按时间排序并过滤
  const filteredEvidences = useMemo(() => {
    let result = [...evidences]
    
    // 搜索过滤
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(e => 
        e.contentRaw?.toLowerCase().includes(query) ||
        e.contentSummary?.toLowerCase().includes(query) ||
        e.extractedTags?.persons?.some(p => p.name.toLowerCase().includes(query)) ||
        e.extractedTags?.facts?.some(f => f.content.toLowerCase().includes(query))
      )
    }
    
    // 信号强度过滤
    if (filterSignal) {
      result = result.filter(e => e.signalStrength === filterSignal)
    }
    
    // 来源类型过滤
    if (filterSource) {
      result = result.filter(e => e.sourceType === filterSource)
    }
    
    // 按时间排序（新的在前）
    result.sort((a, b) => {
      const dateA = new Date(a.occurredAt || a.createdAt)
      const dateB = new Date(b.occurredAt || b.createdAt)
      return dateB.getTime() - dateA.getTime()
    })
    
    return result
  }, [evidences, searchQuery, filterSignal, filterSource])

  // 按月份分组
  const groupedByMonth = useMemo(() => {
    const groups: Record<string, EvidenceItem[]> = {}
    
    filteredEvidences.forEach(evidence => {
      const date = new Date(evidence.occurredAt || evidence.createdAt)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      
      if (!groups[monthKey]) {
        groups[monthKey] = []
      }
      groups[monthKey].push(evidence)
    })
    
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]))
  }, [filteredEvidences])

  const toggleExpand = (id: string) => {
    const newSet = new Set(expandedIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setExpandedIds(newSet)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatMonthLabel = (monthKey: string) => {
    const [year, month] = monthKey.split('-')
    return `${year}年${parseInt(month)}月`
  }

  // 获取唯一的来源类型列表
  const uniqueSourceTypes = useMemo(() => {
    const types = new Set(evidences.map(e => e.sourceType))
    return Array.from(types)
  }, [evidences])

  // 统计信息（支持大小写）
  const stats = useMemo(() => {
    const normalizeStr = (s?: string) => s?.toLowerCase() || ''
    const strongCount = evidences.filter(e => normalizeStr(e.signalStrength) === 'strong').length
    const mediumCount = evidences.filter(e => normalizeStr(e.signalStrength) === 'medium').length
    const weakCount = evidences.filter(e => ['weak', 'none', ''].includes(normalizeStr(e.signalStrength))).length
    const orgLayerCount = evidences.filter(e => normalizeStr(e.demandLayer) === 'organization').length
    
    return { strongCount, mediumCount, weakCount, orgLayerCount, total: evidences.length }
  }, [evidences])

  if (evidences.length === 0) {
    return (
      <div className="text-center py-16">
        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-500 mb-2">暂无证据记录</h3>
        <p className="text-sm text-gray-400">添加第一条证据，开始构建销售五步法地图</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 统计概览 */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <FileText className="w-4 h-4" />
            <span className="text-sm">总证据数</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-100 p-4">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <Zap className="w-4 h-4" />
            <span className="text-sm">强信号</span>
          </div>
          <div className="text-2xl font-bold text-green-700">{stats.strongCount}</div>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm">中信号</span>
          </div>
          <div className="text-2xl font-bold text-amber-700">{stats.mediumCount}</div>
        </div>
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">弱/无信号</span>
          </div>
          <div className="text-2xl font-bold text-gray-700">{stats.weakCount}</div>
        </div>
        <div className="bg-purple-50 rounded-xl border border-purple-100 p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">组织层触达</span>
          </div>
          <div className="text-2xl font-bold text-purple-700">{stats.orgLayerCount}</div>
        </div>
      </div>

      {/* 搜索和过滤 */}
      <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索证据内容、人物、事实..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterSignal}
            onChange={(e) => setFilterSignal(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">所有信号</option>
            <option value="Strong">强信号</option>
            <option value="Medium">中信号</option>
            <option value="Weak">弱信号</option>
            <option value="None">无信号</option>
          </select>
          
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">所有来源</option>
            {uniqueSourceTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 时间线视图 */}
      <div className="relative">
        {/* 时间线轴 */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-200 via-primary-100 to-gray-100" />
        
        {groupedByMonth.map(([monthKey, monthEvidences]) => (
          <div key={monthKey} className="mb-8">
            {/* 月份标签 */}
            <div className="relative flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center z-10">
                <Calendar className="w-5 h-5 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {formatMonthLabel(monthKey)}
              </h3>
              <span className="text-sm text-gray-500">
                共 {monthEvidences.length} 条证据
              </span>
            </div>

            {/* 该月的证据列表 */}
            <div className="space-y-3 ml-6">
              {monthEvidences.map((evidence) => {
                const isExpanded = expandedIds.has(evidence.id)
                const SourceIcon = SOURCE_ICONS[evidence.sourceType] || FileText
                const signalConfig = SIGNAL_COLORS[evidence.signalStrength || 'None']
                const demandConfig = DEMAND_LAYER_COLORS[evidence.demandLayer || 'Solution']

                return (
                  <motion.div
                    key={evidence.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={clsx(
                      'relative bg-white rounded-xl border transition-all',
                      isExpanded ? 'border-primary-200 shadow-md' : 'border-gray-100 hover:border-gray-200'
                    )}
                  >
                    {/* 时间线连接点 */}
                    <div className="absolute -left-4 top-6 w-3 h-3 bg-white border-2 border-primary-400 rounded-full z-10" />

                    {/* 卡片头部 */}
                    <div
                      className="flex items-start gap-4 p-4 cursor-pointer"
                      onClick={() => toggleExpand(evidence.id)}
                    >
                      {/* 来源图标 */}
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <SourceIcon className="w-5 h-5 text-gray-500" />
                      </div>

                      {/* 主要内容 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-gray-500">
                            {formatDate(evidence.occurredAt || evidence.createdAt)}
                          </span>
                          {evidence.sourceFileName && (
                            <span className="text-xs text-gray-400">
                              · {evidence.sourceFileName}
                            </span>
                          )}
                        </div>
                        
                        <p className={clsx(
                          'text-gray-900',
                          !isExpanded && 'line-clamp-2'
                        )}>
                          {evidence.contentSummary || evidence.contentRaw?.slice(0, 200) || '暂无摘要'}
                        </p>

                        {/* 标签行 */}
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          <span className={clsx(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            signalConfig.bg, signalConfig.text
                          )}>
                            {signalConfig.label}
                          </span>
                          <span className={clsx(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            demandConfig.bg, demandConfig.text
                          )}>
                            {demandConfig.label}
                          </span>
                          {evidence.signalType && (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs">
                              {evidence.signalType}
                            </span>
                          )}
                          {evidence.extractedTags?.riskFlags?.map((flag, i) => (
                            <span key={i} className="px-2 py-0.5 bg-red-50 text-red-600 rounded-full text-xs flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {flag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* 展开/收起 */}
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    {/* 展开详情 */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden border-t border-gray-100"
                        >
                          <div className="p-4 space-y-4">
                            {/* 识别到的人物 */}
                            {evidence.extractedTags?.persons && evidence.extractedTags.persons.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  识别到的关键人
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {evidence.extractedTags.persons.map((person, i) => (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg"
                                    >
                                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-xs font-medium">
                                        {person.name.charAt(0)}
                                      </div>
                                      <div className="flex flex-col">
                                        <div className="flex items-center gap-1">
                                          <span className="text-sm text-gray-900">{person.name}</span>
                                          {person.role && (
                                            <span className="text-xs text-gray-500">({person.role})</span>
                                          )}
                                          {person.attitude && (
                                            <span className={clsx(
                                              'text-xs px-1.5 py-0.5 rounded',
                                              person.attitude === '支持' ? 'bg-green-100 text-green-700' :
                                              person.attitude === '反对' ? 'bg-red-100 text-red-700' :
                                              'bg-gray-100 text-gray-600'
                                            )}>
                                              {person.attitude}
                                            </span>
                                          )}
                                        </div>
                                        {(person.title || person.department) && (
                                          <span className="text-xs text-gray-400">
                                            {[person.title, person.department].filter(Boolean).join(' · ')}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* 识别到的事实 */}
                            {evidence.extractedTags?.facts && evidence.extractedTags.facts.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4" />
                                  业务事实
                                </h4>
                                <div className="space-y-2">
                                  {evidence.extractedTags.facts.map((fact, i) => (
                                    <div
                                      key={i}
                                      className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg"
                                    >
                                      <span className={clsx(
                                        'text-xs px-2 py-0.5 rounded font-medium',
                                        fact.category === 'Budget' ? 'bg-green-100 text-green-700' :
                                        fact.category === 'Timeline' ? 'bg-blue-100 text-blue-700' :
                                        fact.category === 'Competitor' ? 'bg-orange-100 text-orange-700' :
                                        fact.category === 'Risk' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-600'
                                      )}>
                                        {fact.category}
                                      </span>
                                      <span className="text-sm text-gray-700 flex-1">{fact.content}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* 原始内容 */}
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">原始内容</h4>
                              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                                {evidence.contentRaw}
                              </div>
                            </div>

                            {/* 操作按钮 */}
                            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                              {onEditEvidence && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onEditEvidence(evidence.id)
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                >
                                  <Edit3 className="w-4 h-4" />
                                  纠正信息
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

